import { format, isValid } from 'date-fns';
import { formatMoney } from '@/lib/money';
import { postTypeLabel } from '@/lib/enums';

/** ISO date-time -> "MMM d, yyyy HH:mm" (falls back to the raw string, then em dash). */
export function formatReleaseTime(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isValid(d) ? format(d, 'MMM d, yyyy HH:mm') : iso;
}

/**
 * Taxonomy category → badge label (camelCase / PascalCase word boundaries).
 * "food" → "Food", "life_style" / "life-style" → "LifeStyle", "ASMR" stays "ASMR".
 */
export function categoryLabel(raw?: string | null): string {
  const s = (raw ?? '').trim();
  if (!s) return '';
  // Preserve short all-caps tokens (ASMR, DIY).
  if (/^[A-Z0-9]{2,6}$/.test(s)) return s;
  const parts = s.replace(/[_-]+/g, ' ').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  return parts
    .map((part) => {
      if (/^[A-Z0-9]{2,6}$/.test(part)) return part;
      const lower = part.toLowerCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join('');
}

// Display-name / format helpers — port of ContentDisplayUtils.kt (doc 08 §4.5).

/** True when media fileType is video (list uses VID; some detail paths use VIDEO). */
export function isVideoFileType(fileType?: string | null): boolean {
  const t = (fileType ?? '').trim().toUpperCase();
  return t === 'VID' || t === 'VIDEO';
}

/**
 * PostType code -> label, via the shared enum registry (N=Post, I=Images, E=Exclusive, S=Story,
 * M=Message, L=Live, F=Flicks, W=Welcome Message). When `fileType` is video, surface that in the
 * label — NORMAL/Post video uploads otherwise look identical to image posts ("Post").
 * Flicks stay "Flicks" (already video-native).
 */
export function contentTypeLabel(postType?: string | null, fileType?: string | null): string {
  const base = postTypeLabel(postType);
  if (!isVideoFileType(fileType)) return base;

  const pt = (postType ?? '').trim().toUpperCase();
  if (pt === 'F' || pt === 'FLICK' || pt === 'FLICKS') return base;
  // Free / normal posts: primary label is Video so the Type column distinguishes them.
  if (pt === '' || pt === 'N' || pt === 'P' || pt === 'POST' || pt === 'NORMAL') return 'Video';
  return `${base} · Video`;
}

/**
 * TipMi publish state only — not Coop. WAT is pre-publish hold ("Waiting"); Coop/pipeline
 * outcome lives in moderationStatus / TipMi ledger (see moderationStatusLabel).
 * UI label: "TipMi visibility".
 */
export function postStatusLabel(s?: string | null): string {
  switch (normalizePostStatusKey(s)) {
    case 'PUB':
      return 'Published';
    case 'HID':
      return 'Hidden';
    case 'WAT':
      return 'Waiting';
    case 'EXP':
      return 'Expired';
    case 'TER':
      return 'Terminated';
    default:
      return 'Unknown';
  }
}

/**
 * Secondary publication hint under Post Status. WAT is held / not live; other codes already
 * encode published vs not in the primary label (Published / Hidden / …).
 */
export function postPublicationHint(s?: string | null): string | null {
  return normalizePostStatusKey(s) === 'WAT' ? 'Not published' : null;
}

/** Fold verbose / mixed-case ledger states down to the enum code. */
export function normalizeModerationStatusKey(s?: string | null): string {
  return (s ?? '').trim().toUpperCase();
}

/**
 * TipMi publication-ledger state (`post_moderation_publication.state`).
 * Null/blank → em dash (no ledger row — legacy or pre-pipeline).
 * UI label: "TipMi ledger". Badge values are prefixed `TipMi · …` so they never
 * collide with Coop / TipMi preview scan wording (`Coop · …` / `TipMi preview · …`
 * via formatVerdictLabel).
 */
export function moderationStatusLabel(s?: string | null): string {
  switch (normalizeModerationStatusKey(s)) {
    case 'PENDING':
      return 'TipMi · Processing';
    case 'APPROVED':
      return 'TipMi · Approved';
    case 'REJECTED':
      return 'TipMi · Rejected';
    case 'HUMAN_REVIEW':
      return 'TipMi · Human review';
    case 'BYPASSED':
      return 'TipMi · Bypassed';
    case 'ERROR':
      return 'TipMi · Error';
    case '':
      return '—';
    default:
      return `TipMi · ${s!.trim()}`;
  }
}

/**
 * Ledger status for TipMi ledger badges.
 *
 * {@code HUMAN_REVIEW} without a Coop risk score/tier is usually a stuck escalate
 * (deadline / GPU wait / classify wedge) on posts that never got a real Coop
 * mid-band review — especially pre-moderation / old content. Treat those as no
 * ledger display (`null` → "—") so Content list does not look like a human queue.
 * Real Coop human-review (with risk) still shows.
 */
export function displayModerationStatus(
  status?: string | null,
  opts?: { combinedRisk?: number | null; riskTier?: string | null },
): string | null {
  const key = normalizeModerationStatusKey(status);
  if (!key) return null;
  if (key === 'HUMAN_REVIEW') {
    const hasRisk =
      opts?.combinedRisk != null || !!(opts?.riskTier ?? '').trim();
    if (!hasRisk) return null;
  }
  return status ?? null;
}

export function moderationStatusBadgeVariant(
  s?: string | null,
): 'default' | 'secondary' | 'destructive' | 'outline' | 'warning' {
  switch (normalizeModerationStatusKey(s)) {
    case 'APPROVED':
    case 'BYPASSED':
      return 'default';
    case 'REJECTED':
    case 'ERROR':
      return 'destructive';
    case 'HUMAN_REVIEW':
      return 'warning';
    case 'PENDING':
      return 'secondary';
    default:
      return 'outline';
  }
}

/** Fold verbose backend variants (PUBLISHED, HIDDEN, ...) down to the short code. */
export function normalizePostStatusKey(s?: string | null): string {
  const v = (s ?? '').trim().toUpperCase();
  switch (v) {
    case 'PUBLISHED':
      return 'PUB';
    case 'HIDDEN':
      return 'HID';
    case 'WAITING':
      return 'WAT';
    case 'EXPIRED':
      return 'EXP';
    case 'TERMINATED':
      return 'TER';
    default:
      return v;
  }
}

/** Badge color per status (parity with CMP status chip palette). */
export function postStatusBadgeVariant(
  s?: string | null,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (normalizePostStatusKey(s)) {
    case 'PUB':
      return 'default';
    case 'HID':
    case 'TER':
      return 'destructive';
    case 'WAT':
    case 'EXP':
      return 'secondary';
    default:
      return 'outline';
  }
}

/** True iff the post is currently hidden (status in HID / HIDDEN) — drives the Take Down toggle. */
export function isPostHidden(status?: string | null): boolean {
  return ['HID', 'HIDDEN'].includes((status ?? '').trim().toUpperCase());
}

/**
 * Money display for price / earnings. CMP renders "$i.ff USD"; we format only at the display
 * site (master D7) through the shared decimal-backed helper. Intl handles the negative sign.
 */
export function formatUsd(n?: number | null): string {
  return formatMoney(n ?? 0, 'USD');
}

/** Compact elapsed label for pending scans (e.g. 45s, 12m, 3h, 2d). */
export function formatElapsedSince(iso?: string | null, nowMs: number = Date.now()): string | null {
  if (!iso) return null;
  const start = new Date(iso).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(nowMs) || start > nowMs) return null;
  const sec = Math.floor((nowMs - start) / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 48) return `${hr}h`;
  return `${Math.floor(hr / 24)}d`;
}

/**
 * Prefer ledger start, then Bedrock attempt, then release/create time.
 * Used to show how long a pending scan has been running.
 */
export function scanProcessingStartedAt(opts: {
  moderationStartedAt?: string | null | undefined;
  bedrockLastAttemptAt?: string | null | undefined;
  releaseTime?: string | null | undefined;
  createdAt?: string | null | undefined;
}): string | null {
  return (
    opts.moderationStartedAt ||
    opts.bedrockLastAttemptAt ||
    opts.releaseTime ||
    opts.createdAt ||
    null
  );
}

/** Prefer ledger error, then Bedrock post-processing error. */
export function scanErrorMessage(opts: {
  moderationLastError?: string | null | undefined;
  postProcessingError?: string | null | undefined;
}): string | null {
  const msg = (opts.moderationLastError || opts.postProcessingError || '').trim();
  return msg || null;
}

/**
 * True when TipMi publication ledger has a row (`moderationStatus` non-blank).
 * Legacy / pre-pipeline posts have no ledger — TipMi ledger UI stays "—".
 * Bedrock classify (`is_add_category` / `post_processing_error`) must never be
 * painted into TipMi ledger cells regardless of this flag.
 */
export function hasTipmiLedger(moderationStatus?: string | null): boolean {
  return normalizeModerationStatusKey(moderationStatus) !== '';
}

export function isClassifyFailed(isAddCategory?: string | null): boolean {
  return (isAddCategory ?? '').trim().toLowerCase() === 'failed';
}

/** @deprecated Prefer {@link isClassifyFailed}. */
export const isBedrockFailed = isClassifyFailed;

/**
 * Engine-aware Failed label for TipMi classify status.
 * Prefer moderationEngine from stack-status; fall back to error text heuristics.
 */
export function classifyFailedLabel(opts: {
  isAddCategory?: string | null;
  postProcessingError?: string | null;
  moderationEngine?: string | null;
}): string | null {
  if (!isClassifyFailed(opts.isAddCategory)) return null;
  const eng = normalizeModerationEngine(opts.moderationEngine);
  if (eng === 'gpu') return 'GPU · Failed';
  if (eng === 'cloud') return 'Cloud · Failed';
  const err = (opts.postProcessingError ?? '').toLowerCase();
  if (err.includes('gpu') || err.includes('moderation-deep')) return 'GPU · Failed';
  if (err.includes('bedrock') || err.includes('cloud')) return 'Cloud · Failed';
  if (eng === 'gpu_with_cloud_fallback') return 'Classify · Failed';
  return 'Classify · Failed';
}

/** Canonical engine wire value; maps legacy taxonomy aliases. */
export function normalizeModerationEngine(raw?: string | null): string {
  const src = (raw ?? '').toLowerCase().trim();
  if (!src) return '';
  if (src === 'gemma' || src === 'gpu') return 'gpu';
  if (src === 'bedrock' || src === 'cloud') return 'cloud';
  if (src === 'dual' || src === 'gpu_with_cloud_fallback') return 'gpu_with_cloud_fallback';
  return src;
}

/** Truncate long scan error strings for list cells. */
export function truncateScanError(msg: string, max = 80): string {
  const t = msg.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}
